import { InnerBox } from "@/components/elements/InnerBox";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider, black, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { Stack, Box, Typography, Grid, Collapse } from "@mui/material";
import { useGetLanguageTestList } from "@/api/services/global/master";
import { EMPTY_STR } from "@/constants/string";
import { useMemo } from "react";
import { IconErrorOutline } from "@/assets/icons";

type TTalentLanguageAbilityProps = {
  defaultValues?: Partial<TTalentForm>;
};

export const TalentLanguageAbility: FCC<TTalentLanguageAbilityProps> = ({ defaultValues }) => {
  const { languageTestLevelOptions } = useGetLanguageTestList();

  const specificBusinessClassificationsStr = defaultValues?.specificBusinessClassifications
    ?.map((item) => item?.companySpecificBusinessClassificationName)
    .filter(Boolean)
    .join("、");

  const languageTestDisplayStr = useMemo(() => {
    if (
      !defaultValues?.languageTestLevelIds ||
      !Array.isArray(defaultValues.languageTestLevelIds) ||
      defaultValues.languageTestLevelIds.length === 0
    ) {
      return defaultValues?.languageTestLevelIdsStr ?? EMPTY_STR.TEXT;
    }

    const labels = defaultValues.languageTestLevelIds
      .map((id) => {
        if (id === null || id === undefined) return null;
        const option = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
        return option?.label ?? null;
      })
      .filter((label): label is string => label !== null);

    return labels.length > 0 ? labels.join("、") : EMPTY_STR.TEXT;
  }, [defaultValues?.languageTestLevelIds, defaultValues?.languageTestLevelIdsStr, languageTestLevelOptions]);

  const isNatTestSelected = useMemo(() => {
    if (
      !defaultValues?.languageTestLevelIds ||
      !Array.isArray(defaultValues.languageTestLevelIds) ||
      defaultValues.languageTestLevelIds.length === 0
    ) {
      return false;
    }
    return defaultValues.languageTestLevelIds.some((id) => {
      if (id === null || id === undefined) return false;
      const selectedOption = languageTestLevelOptions.find((opt) => String(opt.value) === String(id));
      return selectedOption?.label?.includes("NAT-TEST") ?? false;
    });
  }, [defaultValues?.languageTestLevelIds, languageTestLevelOptions]);

  const { expanded: languageExpanded, toggle: languageToggle } = useCollapsible();

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="分野・日本語能力" expanded={languageExpanded} onToggle={languageToggle} />

      <Collapse in={languageExpanded}>
        <Stack mt={3}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="分野" value={defaultValues?.companySpecificIndustry?.companySpecificIndustry} />
            </Grid>

            <Grid item xs={6}>
              <TalentInfoItem label="区分" value={specificBusinessClassificationsStr ?? EMPTY_STR.TEXT} />
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="日本語能力証明書等（実習修了証明含む）" value={languageTestDisplayStr}>
                {isNatTestSelected && (
                  <Typography variant="cap12" color={black[70]}>
                    <Box
                      component="span"
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, color: state.warning_1, whiteSpace: "nowrap", flexWrap: "nowrap" }}
                    >
                      <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
                        <IconErrorOutline fontSize="20px" />
                      </Box>
                      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                        特定技能日本語能力評価対象外（日本語能力レベル参照）
                      </Box>
                    </Box>
                  </Typography>
                )}
              </TalentInfoItem>
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="日本語能力（その他）" value={defaultValues?.languageTestOther ?? EMPTY_STR.TEXT} />
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={12}>
              <TalentInfoItem label="特定技能合格証等" value={defaultValues?.specificSkillCertificateStr} />
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
            <Grid item xs={6}>
              <TalentInfoItem label="技能実習　職種" value={defaultValues?.major?.majorName} isShowDivider={false} />
            </Grid>
            <Grid item xs={6}>
              <TalentInfoItem label="作業" value={defaultValues?.jobType?.jobName} isShowDivider={false} />
            </Grid>
          </Grid>
        </Stack>
      </Collapse>
    </InnerBox>
  );
};
